// Reading extraction state off GET /assignments/{assignmentId}/submissions and
// onto the wizard's pages, so the Converting screen can show text appearing.
//
// This replaces the per-page bulk-draft poll. The unit of extraction state is now
// the ANSWER — one submission reports one status for the student's whole response
// — so every page routed to that student shares its status and its text. That is
// one request per tick for the whole draft, not one per student.
import { isRichTextEmpty } from '../../Stimulus/stimulusHelpers';
import { statusOfExtraction } from './extractionRun';

// A page that failed extraction is finished, not pending: the teacher can type the
// text in at Review. Only PENDING and EXTRACTING still owe us something.
const SETTLED_STATUSES = new Set(['EXTRACTED', 'FAILED', 'SKIPPED']);

const firstAnswer = (submission) => submission?.answers?.[0]?.answer;

/**
 * Two shapes, one meaning. The poll reads
 * GET /assignments/{id}/submissions/status?includeText=true, whose rows expose
 * extraction as {status, failureReason, text}; the one-off roster read that kicks
 * extraction still returns full submissions, where the same three live on
 * answers[0].answer. Everything below this line sees only the first shape.
 */
const extractionOf = (submission) => {
  if (submission?.extraction) return submission.extraction;
  const answer = firstAnswer(submission);
  return {
    status: answer?.extractionStatus,
    failureReason: answer?.extractionFailureReason ?? null,
    text: answer?.answer ?? answer?.text ?? '',
  };
};

export const answerTextOf = (submission) => extractionOf(submission).text ?? '';

/**
 * Everything a page needs from its submission, read once.
 *
 * Every field here belongs to the submission, not the page, but they used to be
 * re-derived per page — and `isRichTextEmpty` strips HTML with three full passes
 * over the answer, so a 1000-page draft re-scanned the same text tens of
 * thousands of times per poll tick.
 */
const derive = (submission) => {
  const extraction = extractionOf(submission);
  const text = extraction.text ?? '';
  return {
    // Statusless-with-text reads as EXTRACTED here for the same reason it does in
    // extractionRun: otherwise the page keeps a falsy status, and Review's chip
    // reads falsy as "still converting" — on a page whose text has already landed.
    status: statusOfExtraction(extraction),
    failureReason: extraction.failureReason ?? null,
    text,
    hasText: !isRichTextEmpty(text),
  };
};

// A page that failed extraction is finished, not pending: the teacher can type the
// text in at Review. Only PENDING and EXTRACTING still owe us something.
const isSettled = (submission) =>
  SETTLED_STATUSES.has(extractionOf(submission).status);

/** An empty roster means the read has not landed yet, not that it is finished. */
export const isExtractionSettled = (submissions) =>
  Array.isArray(submissions) &&
  submissions.length > 0 &&
  submissions.every(isSettled);

/** Settled share of submissions, 0–1 — drives the Converting progress bar. */
export const extractionProgress = (submissions) => {
  const list = Array.isArray(submissions) ? submissions : [];
  if (list.length === 0) return 0;
  return list.filter(isSettled).length / list.length;
};

// An empty server result never clobbers what pdf.js already pulled out locally;
// a non-empty one wins, because server extraction is the authoritative read.
const extractedTextFor = (page, state) =>
  state.hasText ? state.text : page.extractedText;

const isUnchanged = (page, state) =>
  page.extractionStatus === state.status &&
  (page.failureReason ?? null) === state.failureReason &&
  page.extractedText === extractedTextFor(page, state);

const applyTo = (page, state) => {
  if (isUnchanged(page, state)) return page;
  const text = extractedTextFor(page, state);
  return {
    ...page,
    extractionStatus: state.status,
    failureReason: state.failureReason,
    ...(state.hasText ? { extractedText: text, content: text } : {}),
  };
};

// One derived record per submission, shared by all of that student's pages.
const stateByLocalPageId = (submissions, routedPageIdsByStudent) =>
  new Map(
    (submissions || []).flatMap((submission) => {
      const state = derive(submission);
      return (routedPageIdsByStudent?.[String(submission?.studentId)] || []).map(
        (pageId) => [pageId, state],
      );
    }),
  );

export const mapSubmissionsOntoPages = (
  pages,
  routedPageIdsByStudent,
  submissions,
) => {
  const stateFor = stateByLocalPageId(submissions, routedPageIdsByStudent);
  if (stateFor.size === 0) return pages || [];
  let changed = false;
  const next = (pages || []).map((page) => {
    // A typed page's text came from the teacher, not from extraction. It shares a
    // student with scanned pages, so without this the student's extracted text
    // would overwrite what they actually wrote.
    if (page.source === 'manual') return page;
    const state = stateFor.get(page.id);
    const applied = state ? applyTo(page, state) : page;
    if (applied !== page) changed = true;
    return applied;
  });
  return changed ? next : pages;
};
